import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { DataTable } from '@/components/data-display/DataTable';
import { Pagination } from '@/components/data-display/Pagination';
import { Badge, type BadgeVariant } from '@/components/data-display/Badge';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { api } from '@/lib/api-client';
import { useAuth } from '@/hooks/useAuth';
import { useClientList } from './useClientList';
import { NewClientModal } from './create/NewClientModal';
import type { ClientListItem } from './types';

// ---------------------------------------------------------------------------
// KYC badge variant mapping — keyed on webapp label strings (tenant-independent)
// pending=yellow(warning), approved=green(success), rejected=red(error),
// expired=orange — we use 'warning' with a distinct orange for expired
// ---------------------------------------------------------------------------

const KYC_BADGE_VARIANT: Record<string, BadgeVariant> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  expired: 'warning',
};

function KycBadge({ statusName }: { statusName: string }) {
  const key = statusName.toLowerCase();
  const variant = KYC_BADGE_VARIANT[key] ?? 'default';
  const isExpired = key === 'expired';
  return (
    <Badge variant={variant} className={isExpired ? 'bg-[#fff3e0] text-[#bf360c]' : undefined}>
      {statusName}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Risk level badge — keyed on lowercased webapp risk_level_lib labels
// ---------------------------------------------------------------------------

const RISK_BADGE_VARIANT: Record<string, BadgeVariant> = {
  low: 'success',
  standard: 'success',
  medium: 'warning',
  high: 'error',
};

function RiskBadge({ levelName }: { levelName: string }) {
  const variant = RISK_BADGE_VARIANT[levelName.toLowerCase()] ?? 'default';
  return <Badge variant={variant}>{levelName}</Badge>;
}

// ---------------------------------------------------------------------------
// Date formatter
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

// ---------------------------------------------------------------------------
// KYC filter options
// ---------------------------------------------------------------------------

const KYC_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'suspended', label: 'Suspended' },
] as const;

// ---------------------------------------------------------------------------
// Lifecycle filter options
// ---------------------------------------------------------------------------

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Lifecycles' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
] as const;

const STATUS_BADGE_VARIANT: Record<string, BadgeVariant> = {
  draft: 'default',
  pending: 'warning',
  active: 'success',
  suspended: 'error',
};

function StatusBadge({ status }: { status: string }) {
  const variant = STATUS_BADGE_VARIANT[status] ?? 'default';
  return <Badge variant={variant}>{status.charAt(0).toUpperCase() + status.slice(1)}</Badge>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ClientListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showNewClient, setShowNewClient] = useState(false);
  const [resumeDraftId, setResumeDraftId] = useState<number | null>(null);
  const canCreateClient = user?.permissions?.includes('CREATE_CLIENT') ?? false;
  const {
    data,
    isLoading,
    error,
    filters,
    refresh,
    setSearch,
    setKycStatus,
    setStatus,
    setSorting,
    setPage,
    setPageSize,
  } = useClientList();

  const columns = useMemo<ColumnDef<ClientListItem, unknown>[]>(
    () => [
      {
        accessorKey: 'company_name',
        header: 'Client Name',
        enableSorting: true,
        cell: ({ row }) => {
          const name = row.original.display_name || row.original.company_name;
          return (
            <span className="max-w-[300px] truncate font-semibold text-[#333]" title={name}>
              {name}
            </span>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Lifecycle',
        enableSorting: false,
        cell: ({ row }) => {
          const status = row.original.status;
          if (!status) return <span className="text-[#999]">—</span>;
          return <StatusBadge status={status} />;
        },
      },
      {
        accessorKey: 'kyc_status_name',
        header: 'KYC Status',
        enableSorting: false,
        cell: ({ row }) => <KycBadge statusName={row.original.kyc_status_name} />,
      },
      {
        accessorKey: 'risk_level_name',
        header: 'Risk Level',
        enableSorting: false,
        cell: ({ row }) => <RiskBadge levelName={row.original.risk_level_name} />,
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="text-[13px] text-[#666]">{formatDate(row.original.created_at)}</span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => {
          const isDraft = row.original.status === 'draft';
          // Draft actions are gated on CREATE_CLIENT to match the rowClick
          // resume guard; viewers without the permission see View Details.
          if (isDraft && canCreateClient) {
            return (
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (row.original.client_id != null) {
                      setResumeDraftId(row.original.client_id);
                      setShowNewClient(true);
                    }
                  }}
                >
                  Continue
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (row.original.client_id == null) return;
                    const ok = window.confirm(
                      `Delete draft #${row.original.client_id}? This cannot be undone.`,
                    );
                    if (!ok) return;
                    try {
                      await api.delete<void>(`/api/clients/${row.original.client_id}/draft`);
                      toast.success('Draft deleted');
                      refresh();
                    } catch {
                      toast.error('Failed to delete draft');
                    }
                  }}
                >
                  Delete
                </Button>
              </div>
            );
          }
          return (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (row.original.client_id != null) navigate(`/clients/${row.original.client_id}`);
              }}
            >
              View Details
            </Button>
          );
        },
      },
    ],
    [navigate, refresh, canCreateClient],
  );

  // Map filter state to TanStack sorting state
  const sorting: SortingState = [{ id: filters.sort_by, desc: filters.sort_order === 'desc' }];

  const handleSortingChange = (newSorting: SortingState) => {
    if (newSorting.length > 0) {
      const col = newSorting[0];
      if (col.id === 'company_name' || col.id === 'created_at') {
        setSorting(col.id, col.desc ? 'desc' : 'asc');
      }
    }
  };

  const clients = data?.items ?? [];
  const totalRows = data?.total ?? 0;

  return (
    <ErrorBoundary>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-[#333]">Clients</h1>
          {canCreateClient && (
            <Button variant="primary" onClick={() => setShowNewClient(true)}>
              + New client
            </Button>
          )}
        </div>

        <NewClientModal
          open={showNewClient}
          resumeDraftId={resumeDraftId}
          onClose={() => {
            setShowNewClient(false);
            setResumeDraftId(null);
          }}
          onCreated={refresh}
        />

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="w-[300px]">
            <Input
              placeholder="Search by client name..."
              value={filters.search}
              onChange={setSearch}
              aria-label="Search clients"
            />
          </div>
          <select
            value={filters.kyc_status}
            onChange={(e) => setKycStatus(e.target.value as typeof filters.kyc_status)}
            className="rounded-lg border border-[#e0e0e0] bg-white px-4 py-2.5 text-sm text-[#333] outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
            aria-label="Filter by KYC status"
          >
            {KYC_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setStatus(e.target.value as typeof filters.status)}
            className="rounded-lg border border-[#e0e0e0] bg-white px-4 py-2.5 text-sm text-[#333] outline-none transition-colors focus:border-[#6B0F1A] focus:ring-1 focus:ring-[#6B0F1A]"
            aria-label="Filter by lifecycle status"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Error state */}
        {error && !isLoading && (
          <div
            className="rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
            role="alert"
          >
            Failed to load clients. Please try again.
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && clients.length === 0 && (
          <div className="rounded-lg border border-[#e0e0e0] bg-white px-4 py-12 text-center text-sm text-[#999]">
            {filters.status === 'draft'
              ? 'No drafts yet. Click + New client to start one.'
              : 'No clients found. Try adjusting filters.'}
          </div>
        )}

        {/* Table */}
        {(isLoading || clients.length > 0) && (
          <>
            <DataTable<ClientListItem>
              columns={columns}
              data={clients}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              isLoading={isLoading}
              rowClick={(row) => {
                if (row.client_id == null) return;
                if (row.status === 'draft' && canCreateClient) {
                  setResumeDraftId(row.client_id);
                  setShowNewClient(true);
                } else {
                  navigate(`/clients/${row.client_id}`);
                }
              }}
            />
            <Pagination
              pageIndex={filters.page}
              pageSize={filters.page_size}
              totalRows={totalRows}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}
      </div>
    </ErrorBoundary>
  );
}
